import { apiClient, captureRequestScope } from '@/lib/api/api-client'
import { withStatusCommentRetry } from '@/lib/tasks/status-comment'

import type { UpdateClientPortalTaskStatusDTO } from '@scrambl/shared'
import { readStatusCommentRequiredError } from '@scrambl/shared'
import {
  linkAbortSignals,
  SessionChangedError,
} from '@scrambl/shared/session-scope'

/** Keep a comment prompt and its retry owned by the account that initiated it. */
async function sendTransition<T>(
  url: string,
  method: 'PATCH' | 'POST',
  body: object,
  options: {
    signal?: AbortSignal
    statusComment?: string | null
    taskCount?: number
  } = {},
) {
  const capturedBody = { ...body }
  const owner = captureRequestScope()
  if (!owner.actorId) throw new SessionChangedError()
  const linked = linkAbortSignals([owner.signal, options.signal])
  try {
    return await withStatusCommentRetry(
      async statusComment => {
        owner.assertCurrent()
        linked.signal.throwIfAborted()
        let result: T
        try {
          result = await apiClient<T>(url, {
            method,
            body: { ...capturedBody, statusComment },
            signal: linked.signal,
            skipOrgContext: true,
          })
        } catch (error) {
          owner.assertCurrent()
          linked.signal.throwIfAborted()
          if (
            readStatusCommentRequiredError(error) &&
            (!error ||
              typeof error !== 'object' ||
              Reflect.get(error, 'status') !== 400)
          )
            throw new Error(
              'Task change could not be confirmed. Reload before trying again.',
              { cause: error },
            )
          throw error
        }
        owner.assertCurrent()
        linked.signal.throwIfAborted()
        return result
      },
      { statusComment: options.statusComment, taskCount: options.taskCount },
    )
  } finally {
    linked.dispose()
  }
}

export const portalTaskTransitionApi = {
  updateStatus(
    entityId: string,
    projectId: string,
    taskId: string,
    data: UpdateClientPortalTaskStatusDTO,
    options: { signal?: AbortSignal } = {},
  ) {
    return sendTransition(
      `/client-portal/entities/${entityId}/projects/${projectId}/tasks/${taskId}/status`,
      'PATCH',
      data,
      { ...options, statusComment: data.statusComment },
    )
  },
  changeGroup(
    entityId: string,
    projectId: string,
    taskIds: string[],
    groupId: string | null,
    options: {
      signal?: AbortSignal
      expectedRevisions?: Record<string, number>
    } = {},
  ) {
    return sendTransition<{ success: boolean }>(
      `/client-portal/entities/${entityId}/projects/${projectId}/tasks/change-group`,
      'POST',
      {
        taskIds: [...taskIds],
        groupId,
        ...(options.expectedRevisions !== undefined
          ? { expectedRevisions: { ...options.expectedRevisions } }
          : {}),
      },
      { ...options, taskCount: taskIds.length },
    )
  },
}
